import React, { useEffect, useState } from 'react';
import { Styles } from './styles';
import { useParams } from 'react-router-dom';
import useRouteLoading from 'src/hooks/useRouteLoading';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import PropertyCard from '@components/PropertyCard/PropertyCard';
import PageControl from '@components/PageControl';
import { usePropertyStore } from '@stores/Property/Property';
import { IProperty, IPropertySearchParameters } from '@stores/Property/types';
import { getPropertySearchParams } from '@utils/jquery';
import Filter from '@components/Filter';
import { ListTypeEnum } from './types';
import { CardButtons } from './CardButtons';
import { CircularProgress } from '@mui/material';
import { useUserStore } from '@stores/User/User';
import { UserRole } from '@stores/User/types';

const getTitleForListType = (listType?: ListTypeEnum): string => {
  switch (listType) {
    case ListTypeEnum.ATIVOS:
      return 'Exibindo Imóveis Ativos';
    case ListTypeEnum.INATIVOS:
      return 'Exibindo Imóveis Inativos';
    case ListTypeEnum.ARQUIVADOS:
      return 'Exibindo Imóveis Arquivados';
    case ListTypeEnum.NEGOCIANDO:
      return 'Exibindo Imóveis em Negociação';
    case ListTypeEnum.VENDIDOS:
      return 'Exibindo Imóveis Vendidos';
    case ListTypeEnum.ALUGADOS:
      return 'Exibindo Imóveis Alugados';
    case ListTypeEnum.DESTAQUES:
      return 'Exibindo Imóveis em Destaque';
    case ListTypeEnum.APROVANDO:
      return 'Exibindo Imóveis Aguardando Aprovação';
    default:
      return 'Exibindo Imóveis';
  }
};

const PropertyListScreen: React.FC = () => {
  const { listType } = useParams<{ listType?: ListTypeEnum }>() || {
    listType: ListTypeEnum.ATIVOS
  };
  const filters: IPropertySearchParameters = React.useMemo(
    () => getPropertySearchParams(new URLSearchParams(location.search)),
    []
  );
  const { role } = useUserStore();
  const isAdmin = role === UserRole.ADMIN;
  const [properties, setProperties] = useState<IProperty[]>([]);
  const { searchAuthenticatedProperties } = usePropertyStore();
  const loading = useRouteLoading();
  const [isFetching, setIsFetching] = useState(false);

  const [currentPage, setCurrentPage] = useState<number>(filters.page || 1);
  const [lastPage, setLastPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(1);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getPropertyPrice = (property: IProperty): string => {
    const formatBRL = (value: number) =>
      value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    if (property.is_venda && property.valor_substituto) {
      return `a partir de\n${formatBRL(property.valor_substituto)}`;
    }

    const valorExibido = property.valor_venda || property.valor_aluguel || 0;
    return formatBRL(valorExibido);
  };

  useEffect(() => {
    let isMounted = true;
    const fetchProperties = async () => {
      setIsFetching(true);
      const response = await searchAuthenticatedProperties({
        ...filters,
        page: currentPage,
        grupo: listType || ListTypeEnum.ATIVOS
      });

      if (!isMounted) return;

      if (response === null) {
        setProperties([]);
        setLastPage(1);
        setTotal(0);
        setIsFetching(false);
        return;
      }

      if (response.current_page !== currentPage) {
        setCurrentPage(response.current_page);
      }
      setProperties(response.data);
      setLastPage(response.last_page);
      setTotal(response.total);
      setIsFetching(false);
    };
    fetchProperties();
    return () => {
      isMounted = false;
    };
  }, [currentPage, listType, filters, searchAuthenticatedProperties]);

  if (isFetching || loading) {
    return <CircularProgress />;
  }

  return (
    <AuthenticatedLayout>
      <h1>{getTitleForListType(listType)}</h1>
      <Styles.RectangleBar />
      <Filter
        gridDirection="row"
        navigateTo={
          isAdmin
            ? `/area-do-corretor/imoveis/listagem/${listType || 'ativos'}`
            : `/area-do-corretor`
        }
        showSearchField
      />
      <Styles.SectionFilters>
        <Styles.FlexContainer>
          <Styles.RecordsText>Exibindo {total} Registros</Styles.RecordsText>
        </Styles.FlexContainer>
      </Styles.SectionFilters>
      <Styles.PropertyGrid>
        {properties.map((property: IProperty, index: number) => (
          <PropertyCard
            id={property.id}
            key={`${property.id}${index}`}
            imageUrl={property.foto_capa}
            title={property?.titulo || 'Sem título'}
            cod={
              typeof property.cod_imovel === 'string'
                ? property.cod_imovel
                : property.cod_imovel?.cod
            }
            size={`${property.area_construida}`}
            rooms={`${property.quartos}`}
            shower={`${property.banheiros}`}
            garage={`${property.vagas}`}
            propertyType={
              property.tipo_imovel?.titulo ||
              String(property.tipo_imovel || 'Sem tipo')
            }
            operationType={property.is_venda ? 'VENDA' : 'ALUGUEL'}
            price={getPropertyPrice(property)}
            suppressLink={true}
            openModal={true}
            BottomTray={() => (
              <CardButtons listType={listType} property={property} />
            )}
          />
        ))}
      </Styles.PropertyGrid>

      <PageControl
        currentPage={currentPage}
        totalPages={lastPage}
        onChange={handlePageChange}
        syncPageWithJQuery
      />
    </AuthenticatedLayout>
  );
};

export default PropertyListScreen;
